"use client";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ARCHIVED_SESSION_OCCURRENCES,
  RECENT_SESSION_OCCURRENCES,
  ARCHIVED_YEAR_COUNTS,
} from "@/data/derived";
import { isEventPast } from "@/util/date";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { EventsTable } from "@/components/events-table";
import { LoadEventsButton } from "@/components/load-events-button";
import { INITIAL_VISIBLE_EVENTS } from "@/constants/events";
import { cn } from "@/lib/utils";

export function PastTable() {
  const [year, setYear] = useState<string>("all");

  const recentPast = useMemo(
    () =>
      RECENT_SESSION_OCCURRENCES.filter((occurrence) =>
        // Use isEventPast(date, endDate) to calculate cutoff using the client's current timezone
        // instead of relying on build-time computed timestamps which may be UTC.
        isEventPast(occurrence.date, occurrence.endDate),
      ),
    [],
  );

  const events = useMemo(
    () =>
      // Bolt Optimization:
      // Both lists are pre-sorted descending by date (recent from derived, archived from derived).
      // 'recentPast' contains events >= cutoff (2026), 'ARCHIVED' contains < cutoff.
      // Therefore, simple concatenation preserves the descending sort order without O(N log N) resort.
      [...recentPast, ...ARCHIVED_SESSION_OCCURRENCES],
    [recentPast],
  );

  const yearCounts = useMemo(() => {
    const map = new Map(ARCHIVED_YEAR_COUNTS);
    recentPast.forEach((event) => {
      map.set(event.year, (map.get(event.year) || 0) + 1);
    });
    return Array.from(map.entries()).sort(
      (firstYear, secondYear) => Number(secondYear[0]) - Number(firstYear[0]),
    );
  }, [recentPast]);

  const filtered = useMemo(
    () =>
      year === "all" ? events : events.filter((event) => event.year === year),
    [events, year],
  );
  const [allVisible, setAllVisible] = useState(false);
  const shouldShowAll = useMemo(
    () => allVisible || filtered.length <= INITIAL_VISIBLE_EVENTS,
    [allVisible, filtered],
  );
  const visibleEvents = useMemo(
    () =>
      shouldShowAll ? filtered : filtered.slice(0, INITIAL_VISIBLE_EVENTS),
    [filtered, shouldShowAll],
  );

  const prevAllVisible = useRef(allVisible);
  useEffect(() => {
    if (allVisible && !prevAllVisible.current && year === "all") {
      // Focus the first newly visible event row
      const row = document.getElementById(
        `past-event-row-${INITIAL_VISIBLE_EVENTS}`,
      );
      row?.focus();
    }
    prevAllVisible.current = allVisible;
  }, [allVisible, year]);

  const total = filtered.length;
  return (
    <section
      data-section-spy
      id="past"
      aria-labelledby="past-events-heading"
      data-page-gutter
      className="container mx-auto mt-12 scroll-mt-24 sm:mt-16"
    >
      <div
        className="overflow-hidden rounded-xl border"
        style={{ borderColor: "var(--table-border-color)" }}
      >
        <div className="flex flex-row flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-6 sm:py-4">
          <h2
            id="past-events-heading"
            className="text-lg font-semibold tracking-tight sm:text-xl"
          >
            Past events
            <span className="text-muted-foreground ml-2 text-base font-normal">
              ({total})
            </span>
          </h2>
          <div className="relative">
            <Select
              value={year}
              onValueChange={(selectedYear) => {
                setYear(selectedYear);
                if (selectedYear !== "all") {
                  // Ensure smaller year buckets show all events immediately.
                  setAllVisible(true);
                }
              }}
            >
              <SelectTrigger
                className="h-9"
                data-testid="past-events-year-filter"
                label={"Filter past events by year"}
              >
                <SelectValue placeholder="All years" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All years</SelectItem>
                {yearCounts.map(([yearLabel, count]) => (
                  <SelectItem key={yearLabel} value={yearLabel}>
                    {yearLabel} ({count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div
          className={cn(
            "overflow-x-auto contain-[paint]",
            !shouldShowAll && "border-b",
          )}
          style={{ borderBottomColor: "var(--table-border-color)" }}
        >
          <EventsTable events={visibleEvents} idPrefix="past-event-row" />
        </div>
        {!shouldShowAll && (
          <LoadEventsButton
            data-testid="home-load-all-events"
            onClick={() => setAllVisible(true)}
          >
            Load all events
          </LoadEventsButton>
        )}
      </div>
    </section>
  );
}
